import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  CountAndSort,
  PageDescription,
  PageHeader,
  PageTitle,
  ResultsCount,
  Sort,
  CountNumber,
  VehicleListWrapper,
  VehiclesListContainer,
  Titles,
  ShowFiltersAndSort,
  CountAndSortWrapper,
  VehicleListElement,
} from './VehicleList.style';
import FilterTags from '../FilterTags';
import { FilteredVehicle, LocationSearchData } from '../../types';
import VehicleCard from '../VehicleCard';
import SkeletonLoader from '../Skeleton';
import NoVehiclesFound from '../NoVehiclesFound';
import SortBy from '../SortBy';
import LocationFilter from '../Filters/LocationFilter/LocationFilter';
import { selectLocation } from '../../redux/locationReducer';
import { selectAppConfig } from '../../redux/appConfigReducer';
import LoadMore from '../LoadMore';
import useScrollTracker from '../../hooks/useScrollTracker';
import AdditionalCtas from '../AdditionalCtas';
import useFilterTagProcessor from '../../hooks/useFilterTagProcessor';
import useFilterOptions from '../../hooks/useFilterOptions';
import { selectFilters } from '../../redux/filtersReducer';
import FilterIcon from '../../assets/icons/FilterIcon';
import { COLORS } from '../../styles/variables';

interface VehicleListTypes {
  vehiclesList: FilteredVehicle[];
  loading: boolean;
  totalCount: number | null;
  isLaptop: boolean;
  isFiltersOpen: boolean;
  onLocationSearchOpen: () => void;
  setIsFiltersOpen: (arg: boolean) => void;
  fetchMore: (args: { variables: any; updateQuery: any }) => void;
}

const VehicleList: React.FC<VehicleListTypes> = ({
  vehiclesList,
  loading,
  totalCount,
  isLaptop,
  isFiltersOpen,
  onLocationSearchOpen,
  setIsFiltersOpen,
  fetchMore,
}) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { location } = useSelector(selectLocation);
  const { marketType, isTier3page, isImageGalleryOn, listOfDealers, pageTitle, pageDescription } =
    useSelector(selectAppConfig);
  const { confirmedFilters } = useSelector(selectFilters);
  const { listOfAllFilters } = useFilterOptions();

  const { filterCount } = useFilterTagProcessor(confirmedFilters, listOfAllFilters, isTier3page);

  const { t, i18n } = useTranslation();
  const filteredVehicles = isTier3page
    ? vehiclesList
    : vehiclesList.filter((toCheck) => {
        if (marketType === 'USA' && toCheck.modelPrice === null) return false;
        const typedPrices = toCheck?.typedPrices;
        const retailPriceAmount = typedPrices?.find(
          (priceItem) => priceItem?.type === 'retail',
        )?.amount;
        if (marketType === 'CAN' && (!retailPriceAmount || retailPriceAmount === 0)) return false;
        if (marketType === 'CAN' && retailPriceAmount && retailPriceAmount < 0) return false;
        return true;
      });
  const shouldShowLoadMore = totalCount && vehiclesList.length < totalCount;

  const scrolled = useScrollTracker(236);

  const cardsToShow = 12;

  const mainDealer = listOfDealers?.find((dealer) => dealer.isMainDealer)?.dealerId;

  const renderContent = useMemo(() => {
    if (loading && !isLoadingMore) {
      return (
        <VehiclesListContainer>
          {Array.from({ length: cardsToShow })
            .fill(null)
            .map((_, index) => (
              /* eslint-disable-next-line react/no-array-index-key */
              <SkeletonLoader key={index} />
            ))}
        </VehiclesListContainer>
      );
    }
    if (!loading && !vehiclesList?.length) {
      // hide CTAs for Tier3 Temporarily
      return <NoVehiclesFound noCtas={isTier3page} onLocationSearchOpen={onLocationSearchOpen} />;
    }
    return (
      <VehiclesListContainer scrolled={scrolled}>
        <VehicleListElement>
          {filteredVehicles.map((vehicle) => (
            <VehicleCard
              vehicle={vehicle}
              key={`${vehicle.id}.${vehicle.kvpsSyncId}`}
              isTier3page={isTier3page}
              marketType={marketType}
              isImageGalleryOn={isImageGalleryOn}
              mainDealer={mainDealer}
            />
          ))}
        </VehicleListElement>
      </VehiclesListContainer>
    );
  }, [
    filteredVehicles,
    isImageGalleryOn,
    isLoadingMore,
    isTier3page,
    loading,
    mainDealer,
    marketType,
    onLocationSearchOpen,
    scrolled,
    vehiclesList?.length,
  ]);

  const renderSortBy = useMemo(() => {
    if (isLaptop)
      return (
        <Sort>
          <SortBy />
        </Sort>
      );
    return (
      <ShowFiltersAndSort onClick={() => setIsFiltersOpen(true)}>
        <FilterIcon color={COLORS.black} />
        {t('vlp.vehicleResults.sortAndFilter')} ({filterCount})
      </ShowFiltersAndSort>
    );
  }, [filterCount, isLaptop, setIsFiltersOpen, t]);

  const renderTitle = useMemo(() => {
    if (isTier3page && pageTitle?.length) {
      return pageTitle;
    }
    // Tier 1
    return t('vlp.vehicleResults.pageTitle');
  }, [isTier3page, pageTitle, t]);

  const renderDescription = useMemo(() => {
    if (isTier3page && pageDescription?.length) {
      return pageDescription;
    }
    if (isTier3page && !pageDescription?.length) {
      return '';
    }
    // Tier 1
    return t('vlp.vehicleResults.pageDescription');
  }, [isTier3page, pageDescription, t]);

  return (
    <VehicleListWrapper isFiltersOpen={isFiltersOpen}>
      <PageHeader isFiltersOpen={isFiltersOpen}>
        {!isLaptop && !isTier3page && (
          <LocationFilter
            userSelectedLocation={location as LocationSearchData}
            market={marketType}
            isLaptop={isLaptop}
          />
        )}
        <Titles>
          <PageTitle>{renderTitle}</PageTitle>
          <PageDescription>{renderDescription}</PageDescription>
        </Titles>
        {isLaptop && <FilterTags />}
        <CountAndSortWrapper scrolled={scrolled}>
          <CountAndSort>
            <ResultsCount>
              <CountNumber data-testid="vehicle-count">{loading ? '##' : totalCount}</CountNumber>
              {t('vlp.vehicleResults.totalResults')}
            </ResultsCount>
            {renderSortBy}
          </CountAndSort>
          {!isLaptop && <FilterTags />}
        </CountAndSortWrapper>
      </PageHeader>
      {renderContent}
      {shouldShowLoadMore ? (
        <LoadMore
          fetchMore={fetchMore}
          vehiclesList={vehiclesList}
          isLoadingMore={isLoadingMore}
          setIsLoadingMore={setIsLoadingMore}
        />
      ) : null}
      {!isTier3page && <AdditionalCtas />}
    </VehicleListWrapper>
  );
};

export default VehicleList;
